// src/Settings.js
import { useState, useEffect, useMemo } from 'react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
import { FileDown } from 'lucide-react';
import TitleBar from './components/ui/TitleBar';
import BackToDashboardButton from './components/ui/BackToDashboardButton';
import SavedToastOverlay from './components/SavedToastOverlay';
import { MODULE_MAP } from './constants';

import BusinessSettings from './BusinessSettings';
import HoursSettings from './HoursSettings';
import TaxesSettings from './TaxesSettings';
import DiscountsCouponsSettings from './DiscountsCouponsSettings';
import BrandingSettings from './BrandingSettings';
import EmployeesSettings from './EmployeesSettings';
import VendorsSettings from './VendorsSettings';
import SocialMediaSettings from './SocialMediaSettings';
import AppointmentTypesSettings from './AppointmentTypesSettings';
import APIKeysSettings from './APIKeysSettings';
import SubscriptionSettings from './SubscriptionSettings';
import ReportsSettings from './ReportsSettings';
import SavedNotesSettings from './SavedNotesSettings';
import VehiclesSettings from './VehiclesSettings';


const Settings = ({ defaultModule, reportId, onReportOpened, setupProgress }) => {
  const [activeModule, setActiveModule] = useState(defaultModule || null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isPhone, setIsPhone] = useState(window.innerWidth < 768);

  // backup reminder + export modal
  const [showExportReminder, setShowExportReminder] = useState(true);
  const [lastExportDate, setLastExportDate] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('excel');
  const [exporting, setExporting] = useState(false);

  const API_BASE = useMemo(() => getApiBase(), []);

  const settingsModules = [
    { id: 'appointments', name: 'APPOINTMENT TYPES', component: AppointmentTypesSettings },
    { id: 'branding', name: 'BRANDING', component: BrandingSettings },
    { id: 'business', name: 'BUSINESS', component: BusinessSettings },
    { id: 'discounts', name: 'DISCOUNTS & COUPONS', component: DiscountsCouponsSettings },
    { id: 'employees', name: 'EMPLOYEES', component: EmployeesSettings },
    { id: 'hours', name: 'HOURS', component: HoursSettings },
    { id: 'api', name: 'PAYMENT SETUP', component: APIKeysSettings },
    { id: 'reports', name: 'REPORTS', component: ReportsSettings },
    { id: 'saved-notes', name: 'SAVED NOTES', component: SavedNotesSettings },
    { id: 'social', name: 'SOCIAL MEDIA', component: SocialMediaSettings },
    { id: 'subscription', name: 'SUBSCRIPTION', component: SubscriptionSettings },
    { id: 'taxes', name: 'TAXES SETUP', component: TaxesSettings },
    { id: 'vehicles', name: 'VEHICLES', component: VehiclesSettings },
    { id: 'vendors', name: 'VENDORS/SUPPLIERS', component: VendorsSettings }
  ];

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 1024);
      setIsPhone(window.innerWidth < 768);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeModule]);

  useEffect(() => {
    if (defaultModule) setActiveModule(defaultModule);
  }, [defaultModule]);

  useEffect(() => {
    const loadExportAudit = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/export/audit`, {
          headers: { ...getTenantHeaders() }
        });
        const data = await r.json().catch(() => null);
        if (data?.last_export_at) setLastExportDate(new Date(data.last_export_at));
      } catch (_e) {}
    };
    loadExportAudit();
  }, [API_BASE]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_BASE}/api/export?format=${exportFormat}`, {
        headers: { ...getTenantHeaders() }
      });
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `mytoolnow-backup-${new Date().toISOString().split('T')[0]}.${
        exportFormat === 'excel' ? 'xlsx' : exportFormat === 'csv' ? 'zip' : 'pdf'
      }`;

      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setShowExportModal(false);

      try {
        const auditResponse = await fetch(`${API_BASE}/api/export/audit`, {
          headers: { ...getTenantHeaders() }
        });
        const auditData = await auditResponse.json().catch(() => null);
        if (auditData?.last_export_at) setLastExportDate(new Date(auditData.last_export_at));
      } catch (_e) {}
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // GLOBAL WRAPPER STYLES FOR ALL MODULES
  const moduleWrapperStyles = {
    backgroundColor: '#E8E2DA',
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: isPhone ? '0 2px' : (isMobile ? '0px' : '20px')
  };

  const moduleContainerStyles = {
    width: '100%',
    maxWidth: '1000px',
    padding: isPhone ? '0' : '0 12px',
    boxSizing: 'border-box'
  };

  const setupMeter = setupProgress ? (
    <div style={{ width: isMobile ? '100%' : '240px' }}>
      <div style={{
        fontWeight: 900, fontSize: '11px', color: '#2D5F3F',
        textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px'
      }}>
        PROFILE SETUP {setupProgress.completedCount} / {setupProgress.totalCount}
      </div>
      <div style={{ display: 'flex', gap: '3px' }}>
        {Object.entries(setupProgress.modules).map(([key, done]) => {
          const allComplete = setupProgress.completedCount === setupProgress.totalCount;
          const color = allComplete ? '#2D5F3F' : (done ? '#ef4444' : '#ccc');
          return !done && !allComplete ? (
            <button
              key={key}
              onClick={() => setActiveModule(MODULE_MAP[key])}
              style={{ flex: 1, height: isMobile ? '6px' : '10px', minHeight: 0, minWidth: 0, borderRadius: '2px', backgroundColor: color, cursor: 'pointer', border: 'none', padding: 0 }}
            />
          ) : (
            <div key={key} style={{ flex: 1, height: isMobile ? '6px' : '10px', borderRadius: '2px', backgroundColor: color }} />
          );
        })}
      </div>
    </div>
  ) : null;

  if (activeModule) {
    const ActiveComponent = settingsModules.find((m) => m.id === activeModule)?.component;

    const navigateToSettings = (path) => {
      if (path === 'settings' || path === '/settings') {
        setActiveModule(null);
      }
    };

    return ActiveComponent ? (
      <>
        {activeModule !== 'api' && activeModule !== 'subscription' && (
          <SavedToastOverlay accentColor="#E8761D" />
        )}

        <div style={moduleWrapperStyles}>
          <div style={moduleContainerStyles}>
            <ActiveComponent navigate={navigateToSettings} isPhone={isPhone} reportId={activeModule === 'reports' ? reportId : null} onReportOpened={onReportOpened} />
          </div>
        </div>
      </>
    ) : null;
  }

  return (
    <>
      <SavedToastOverlay accentColor="#E8761D" />

      <style>{`
        .jfn-settings-page {
          min-height: 100vh;
          background: #E8E2DA;
          padding: 40px 24px 70px 24px;
          box-sizing: border-box;
        }
        .jfn-settings-inner {
          width: 100%;
          max-width: 1120px;
          margin: 0 auto;
        }
        .jfn-export-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
          padding: 14px 24px;
          background: #000;
          color: #FFA500;
          font-weight: 900;
          cursor: pointer;
          text-transform: uppercase;
          font-size: 16px;
          border: 4px solid #E8761D;
          transition: all 0.12s ease;
        }
        .jfn-export-btn:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
        }
        .jfn-settings-actions {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          width: 100%;
          margin-bottom: 22px;
        }
        .jfn-settings-back-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
          background-color: #E8761D !important;
          color: #000 !important;
          border-color: #000 !important;
        }
        .jfn-settings-back-btn:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #000 !important;
        }
        .jfn-settings-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 22px;
          margin-top: 22px;
        }
        .jfn-settings-tile {
          width: 100%;
          height: 110px;
          border: 7px solid #E8761D;
          background: linear-gradient(135deg, #0b0b0b 0%, #000 55%, #111 100%);
          cursor: pointer;
          padding: 0 18px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-sizing: border-box;
          transition: transform 160ms ease;
        }
        .jfn-settings-tile:hover {
          border-color: #FFC107;
          transform: translateY(-1px);
        }
        .jfn-settings-iconWrap {
          width: 48px;
          height: 48px;
          border-radius: 10px;
          border: 3px solid #E8761D;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #0a0a0a;
          flex: 0 0 auto;
          overflow: hidden;
        }
        .jfn-settings-icon {
          width: 46px;
          height: 46px;
          object-fit: cover;
          display: block;
        }
        .jfn-settings-tileText {
          font-weight: 900;
          font-size: 16px;
          text-transform: uppercase;
          color: #fff;
          white-space: nowrap;
        }
        .jfn-export-option {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 14px 14px;
          border: 4px solid #000;
          cursor: pointer;
          font-weight: 900;
          text-transform: uppercase;
          user-select: none;
        }
        .jfn-export-option input {
          transform: scale(1.2);
          cursor: pointer;
        }
        .jfn-modal-cancel:hover,
        .jfn-modal-export:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
        @media (max-width: 980px) {
          .jfn-settings-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 767px) {
          .jfn-settings-actions { grid-template-columns: 1fr; }
        }
      `}</style>

      <div
        className="jfn-settings-page"
        style={isPhone ? { paddingLeft: '6px', paddingRight: '6px' } : undefined}
      >
        <div className="jfn-settings-inner">
          <TitleBar
            icon="⚙️"
            title="SETTINGS"
            accent="#E8761D"
            wrapStyle={{ border: '12px solid black', boxSizing: 'border-box' }}
            topRow={isMobile ? setupMeter : null}
            right={isMobile ? null : setupMeter}
          />

          <div className="jfn-settings-actions">
            <button onClick={() => setShowExportModal(true)} className="jfn-export-btn">
              <FileDown size={20} />
              EXPORT DATA
            </button>
            <BackToDashboardButton
              className="jfn-settings-back-btn"
              onClick={() => (window.location.href = '/dashboard')}
            />
          </div>

          {showExportReminder && (
            <div
              style={{
                backgroundColor: '#fff7ed',
                border: '6px solid #000',
                marginBottom: '22px',
                padding: isMobile ? '12px 40px 12px 14px' : '14px 50px 14px 18px',
                boxSizing: 'border-box',
                width: '100%',
                position: 'relative'
              }}
            >
              <p
                style={{
                  margin: 0,
                  padding: '8px 10px',
                  border: '3px solid #E8761D',
                  borderRadius: '6px',
                  color: 'black',
                  fontWeight: 900,
                  fontSize: isMobile ? '13px' : '15px',
                  lineHeight: '1.5'
                }}
              >
                ⚠️ 💾 WARNING — BACK UP YOUR DATA! &nbsp;|&nbsp; It is always in your best interest to export your data at the end
                of every week to protect against unforeseen data loss. Simply overwrite your previously saved file with the latest
                export.{lastExportDate ? ` Last backup: ${lastExportDate.toLocaleDateString()}.` : ''}
              </p>
              <button
                onClick={() => setShowExportReminder(false)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'black',
                  fontWeight: 900,
                  fontSize: '20px',
                  zIndex: 10
                }}
              >
                ✕
              </button>
            </div>
          )}

          {showExportModal && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 5000
              }}
            >
              <div
                style={{
                  backgroundColor: 'white',
                  padding: '30px',
                  maxWidth: '520px',
                  width: '100%',
                  border: '6px solid black'
                }}
              >
                <h2 style={{ fontWeight: 900, textTransform: 'uppercase', marginTop: 0 }}>EXPORT DATA</h2>

                <p style={{ marginBottom: '8px', fontWeight: 800, color: '#333' }}>Choose export format:</p>

                <p
                  style={{
                    marginBottom: '14px',
                    fontWeight: 700,
                    color: '#856404',
                    backgroundColor: '#fff3cd',
                    border: '3px solid #856404',
                    padding: '10px'
                  }}
                >
                  ⚠️ NOTE: Each new export will overwrite your previous download with the most current data. Keep your latest backup stored
                  somewhere safe.
                </p>

                <div style={{ display: 'grid', gap: '12px' }}>
                  <label
                    className="jfn-export-option"
                    style={{
                      backgroundColor: exportFormat === 'excel' ? '#E8F3EC' : 'white',
                      borderColor: exportFormat === 'excel' ? '#2D5F3F' : '#000'
                    }}
                  >
                    <span>EXCEL (.XLSX)</span>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="excel"
                      checked={exportFormat === 'excel'}
                      onChange={(e) => setExportFormat(e.target.value)}
                    />
                  </label>

                  <label
                    className="jfn-export-option"
                    style={{
                      backgroundColor: exportFormat === 'csv' ? '#E8F3EC' : 'white',
                      borderColor: exportFormat === 'csv' ? '#2D5F3F' : '#000'
                    }}
                  >
                    <span>CSV (ZIP)</span>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={(e) => setExportFormat(e.target.value)}
                    />
                  </label>

                  <label
                    className="jfn-export-option"
                    style={{
                      backgroundColor: exportFormat === 'pdf' ? '#E8F3EC' : 'white',
                      borderColor: exportFormat === 'pdf' ? '#2D5F3F' : '#000'
                    }}
                  >
                    <span>PDF</span>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="pdf"
                      checked={exportFormat === 'pdf'}
                      onChange={(e) => setExportFormat(e.target.value)}
                    />
                  </label>
                </div>

                {exporting && (
                  <p style={{ margin: '16px 0 0', fontWeight: 900, color: '#2D5F3F' }}>
                    EXPORT DATA GATHERING HAS STARTED. THIS COULD TAKE A MOMENT.
                  </p>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button
                    className="jfn-modal-cancel"
                    onClick={() => setShowExportModal(false)}
                    style={{ flex: 1, padding: '14px', border: '4px solid black', fontWeight: 900, cursor: 'pointer' }}
                  >
                    CANCEL
                  </button>
                  <button
                    className="jfn-modal-export"
                    onClick={handleExport}
                    disabled={exporting}
                    style={{
                      flex: 1,
                      padding: '14px',
                      backgroundColor: '#000',
                      color: '#FFA500',
                      border: '4px solid #E8761D',
                      fontWeight: 900,
                      cursor: exporting ? 'not-allowed' : 'pointer',
                      opacity: exporting ? 0.65 : 1
                    }}
                  >
                    EXPORT
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="jfn-settings-grid">
            {settingsModules.map((module) => (
              <button key={module.id} onClick={() => setActiveModule(module.id)} className="jfn-settings-tile">
                <div className="jfn-settings-iconWrap">
                  <img className="jfn-settings-icon" src={`${process.env.PUBLIC_URL}/mtnappicon.png`} alt="MyTool NOW" />
                </div>
                <div className="jfn-settings-tileText">{module.name}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;
